import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import InstallBonus from "@/components/InstallBonus";
import CmsSEO from "@/components/CmsSEO";
import RouteSEO from "@/components/RouteSEO";
import Home from "@/pages/Home";
import Schedule from "@/pages/Schedule";
import QuickPay from "@/pages/QuickPay";
import Checkout from "@/pages/Checkout";
import Confirmation from "@/pages/Confirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import WorkerView from "@/pages/WorkerView";
import Rules from "@/pages/Rules";
import CalendarPage from "@/pages/CalendarPage";
import PlanPage from "@/pages/PlanPage";
import BillingPage from "@/pages/BillingPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import MapPage from "@/pages/MapPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import ReviewsPage from "@/pages/ReviewsPage";
import NewReviewPage from "@/pages/NewReviewPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CommunityPage from "@/pages/CommunityPage";
import ContestPage from "@/pages/ContestPage";
import PoliciesPage from "@/pages/PoliciesPage";
import SettingsPage from "@/pages/SettingsPage";
import EditorLayout from "@/pages/editor/EditorLayout";
import EditorIndex from "@/pages/editor/EditorIndex";
import EditorPage from "@/pages/editor/EditorPage";
import EditorMedia from "@/pages/editor/EditorMedia";
import "@/App.css";

function Protected({ children, role, anyRole }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = anyRole || (role ? [role] : null);
  if (allowed && !allowed.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Ops Admin: pure `admin`, `superadmin`, `parent`. Explicitly excludes content_editor.
const OPS_ADMIN_ROLES = ["admin", "superadmin", "parent"];
const STAFF_WORKER = ["admin", "superadmin", "parent", "kid_worker"];
// Website Editor (CMS): content_editor + owner roles. Pure admin EXCLUDED.
const CMS_ROLES = ["content_editor", "superadmin", "parent"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <InstallBonus />
        <RouteSEO />
        <Routes>
          {/* Website Editor uses its OWN top-level layout, completely separate
              from the operational app shell (Navbar/Footer). */}
          <Route
            path="/editor"
            element={
              <Protected anyRole={CMS_ROLES}>
                <EditorLayout />
              </Protected>
            }
          >
            <Route index element={<EditorIndex />} />
            <Route path="page/:slug" element={<EditorPage />} />
            <Route path="media" element={<EditorMedia />} />
          </Route>
          {/* Legacy /cms alias → /editor (per separation plan) */}
          <Route path="/cms" element={<Navigate to="/editor" replace />} />

          {/* Everything else uses the public app Layout (Navbar + Footer) */}
          <Route
            path="*"
            element={
              <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/schedule" element={<Protected anyRole={["customer","admin","superadmin","parent"]}><Schedule /></Protected>} />
                  <Route path="/quick-pay" element={<QuickPay />} />
                  <Route path="/quick-pay/done" element={<QuickPay />} />
                  <Route path="/checkout/:orderId" element={<Protected><Checkout /></Protected>} />
                  <Route path="/confirmation" element={<Protected><Confirmation /></Protected>} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/dashboard" element={<Protected role="customer"><CustomerDashboard /></Protected>} />
                  <Route path="/calendar" element={<Protected role="customer"><CalendarPage /></Protected>} />
                  <Route path="/plan" element={<Protected role="customer"><PlanPage /></Protected>} />
                  <Route path="/billing" element={<Protected role="customer"><BillingPage /></Protected>} />
                  <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
                  <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
                  <Route path="/community" element={<Protected role="customer"><CommunityPage /></Protected>} />
                  <Route path="/contest" element={<ContestPage />} />
                  <Route path="/policies" element={<PoliciesPage />} />
                  <Route path="/settings" element={<Protected role="customer"><SettingsPage /></Protected>} />
                  <Route path="/reviews" element={<ReviewsPage />} />
                  <Route path="/reviews/new" element={<Protected role="customer"><NewReviewPage /></Protected>} />
                  <Route path="/map" element={<MapPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/admin" element={<Protected anyRole={OPS_ADMIN_ROLES}><AdminDashboard /></Protected>} />
                  <Route path="/worker" element={<Protected anyRole={STAFF_WORKER}><WorkerView /></Protected>} />
                  <Route path="/rules" element={<Rules />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
